import type { ReactNode } from 'react';

import { TabsContent, TabsTrigger, TabSelectionIndicator, cn } from '@shigo-ui/ui';

import { useDashboardPageFormTabs } from './dashboardPageFormTabsContext';

export interface DashboardPageFormTabProps {
  children: ReactNode;
  icon?: ReactNode | undefined;
  value: string;
  errorCount?: number | undefined;
  errorLabel?: ((count: number) => string) | undefined;
  description?: string | undefined;
}

export function DashboardPageFormTab({
  children,
  icon,
  value,
  errorCount = 0,
  errorLabel,
  description,
}: DashboardPageFormTabProps) {
  const context = useDashboardPageFormTabs();
  return (
    <TabsTrigger
      value={value}
      data-section={value}
      title={description}
      aria-label={typeof children === 'string' ? children : undefined}
      aria-description={
        errorCount > 0 ? (errorLabel?.(errorCount) ?? String(errorCount)) : undefined
      }
      className={cn(
        'data-[state=active]:text-foreground text-muted-foreground relative max-w-full min-w-0 shrink-0 gap-2 text-[13px] shadow-none data-[state=active]:shadow-none',
        context.sidebar
          ? 'group/section hover:text-foreground data-[state=active]:bg-card min-w-0 justify-start gap-3 rounded-md px-3 py-2.5 text-left whitespace-normal transition-colors motion-reduce:transition-none'
          : 'rounded-none bg-transparent px-2 data-[state=active]:bg-transparent sm:px-3',
      )}
    >
      {context.sidebar && context.value === value ? (
        <TabSelectionIndicator
          layoutId={context.indicatorId}
          className="inset-y-3 left-0 w-0.5 max-md:inset-x-3 max-md:inset-y-auto max-md:bottom-0 max-md:h-0.5 max-md:w-auto"
        />
      ) : null}
      {icon ? (
        <span aria-hidden="true" className="shrink-0 [&>svg]:size-4">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className={cn('block', 'break-words whitespace-normal')}>{children}</span>
        {context.sidebar && description ? (
          <span className="text-muted-foreground mt-1 hidden text-xs leading-relaxed font-normal md:group-data-[state=active]/section:block">
            {description}
          </span>
        ) : null}
      </span>
      {errorCount > 0 ? (
        <span
          className="bg-destructive/10 text-destructive inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-semibold tabular-nums"
          aria-hidden="true"
        >
          {errorCount}
        </span>
      ) : null}
    </TabsTrigger>
  );
}

export function DashboardPageFormTabPanel({
  children,
  value,
  className,
}: {
  children: ReactNode;
  value: string;
  className?: string | undefined;
}) {
  const { sidebar } = useDashboardPageFormTabs();
  return (
    <TabsContent
      value={value}
      className={cn(
        'mt-0 min-w-0',
        sidebar &&
          'md:col-start-2 md:row-start-1 [&_[data-slot=dashboard-page-form-body]]:mx-0 [&_[data-slot=dashboard-page-form-body]]:max-w-none md:[&_[data-slot=dashboard-page-form-body]]:px-8',
        className,
      )}
    >
      {children}
    </TabsContent>
  );
}
